import { Fragment, useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import * as Step from 'root/src/client/web/giveaway/components/Step';
import * as Carousel from 'root/src/client/v2/common/components/Carousel/Carousel';
import Autoplay from 'embla-carousel-autoplay';
import { ButtonNew } from 'root/src/client/v2/common/components/form/ButtonNew';
import SilverCup from 'root/src/client/web/giveaway/assets/ic-silver-cup.svg';
import ErrorIcon from 'root/src/client/web/giveaway/assets/ic-error.svg';
import SuccessIcon from 'root/src/client/web/giveaway/assets/ic-success.svg';
import { useVerifySteamModal } from 'root/src/client/web/giveaway/features/VerifySteamModal/hooks/useVerifySteamModal';
import { steps } from 'root/src/client/web/giveaway/features/VerifySteamModal/fixtures/steps';
import { SteamErrorCode, VerifySteamStage } from 'root/src/shared/giveaways/giveaways.constants';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import { useVerifySteamStageSuccess } from 'root/src/client/web/giveaway/features/VerifySteamModal/hooks/useVerifySteamStageSuccess';
import { useVerifySteamStageError } from 'root/src/client/web/giveaway/features/VerifySteamModal/hooks/useVerifySteamStageError';
import SteamPrivacySettingsExample from 'root/src/client/web/giveaway/assets/steam-privacy-settings-example.png';
import { SignInWithSteam } from 'root/src/client/web/giveaway/features/GiveawaySection/ViewerGiveawaySection';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
const VerifySteamModal = ({ stage, error, code }) => {
    return (<ModalLayout>
			<div className="flex flex-col gap-10 justify-center items-center w-full">
				<div className="flex flex-col gap-2 justify-center items-center">
					<h1 className="uppercase font-mono font-bold text-xl md:text-3xl text-center">Verify Steam account</h1>
					<span className="text-sm sm:text-base text-light-4 text-center">
						Ready to enter the giveaway?<br />
						Authorize your STEAM account for a chance to win cool rewards!
					</span>
				</div>
				<VerifySteamSteps />
				<VerifySteamStageSection stage={stage} error={error} code={code}/>
			</div>
		</ModalLayout>);
};
const useSelectedSnap = () => {
    const { emblaApi } = Carousel.useCarouselContext();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const onSelect = useCallback((api) => {
        setSelectedIndex(api.selectedScrollSnap());
    }, []);
    useEffect(() => {
        if (!emblaApi)
            return;
        onSelect(emblaApi);
        emblaApi.on('select', onSelect);
    }, [emblaApi, onSelect]);
    return { selectedIndex };
};
const StepDots = () => {
    const { emblaApi } = Carousel.useCarouselContext();
    const { selectedIndex } = useSelectedSnap();
    return (<div className="flex justify-center gap-1.5">
			{steps.map((step, index) => (<button aria-label="Step dot" type="button" key={step.description} onClick={() => emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollTo(index)} className={`h-1.5 rounded-full transition-all duration-300 ${index === selectedIndex ? 'w-6 bg-teal' : 'w-1.5 bg-light-4/30'}`}/>))}
		</div>);
};
const VerifySteamStepsMobile = () => {
    return (<div className="flex sm:hidden flex-col items-center gap-2 self-stretch overflow-hidden">
			<Carousel.Root loop cssTransition className="max-w-full" plugins={[Autoplay({ delay: 3000, stopOnInteraction: false })]}>
				<div className="flex items-center justify-center gap-2">
					<Carousel.PrevButton className="shrink-0 text-light-4 hover:text-teal transition-colors text-2xl font-bold px-2">
						&#x2039;
					</Carousel.PrevButton>
					<div className="flex-1 min-w-0 overflow-hidden">
						<Carousel.Container className="flex flex-initial">
							{steps.map((step, index) => (<Carousel.Slide key={step.description} width="90%">
									<div className="flex justify-center items-center gap-4">
										<span className="font-mono font-bold text-teal text-sm shrink-0">
											{index + 1}/{steps.length}
										</span>
										<span className="text-sm text-light-4 text-center whitespace-pre-line">
											{step.description}
										</span>
									</div>
								</Carousel.Slide>))}
						</Carousel.Container>
					</div>
					<Carousel.NextButton className="shrink-0 text-light-4 hover:text-teal transition-colors text-2xl font-bold px-2">
						&#x203A;
					</Carousel.NextButton>
				</div>
				<div className="flex justify-center mt-2">
					<StepDots />
				</div>
			</Carousel.Root>
		</div>);
};
const VerifySteamStepsDesktop = () => {
    return (<div className="hidden sm:flex flex-row justify-center gap-2 items-center">
			{steps.map((step, index) => (<Fragment key={step.description}>
					<Step.Root>
						<Step.Indicator>
							{index + 1}
						</Step.Indicator>
						<Step.Description>
							{step.description}
						</Step.Description>
					</Step.Root>
					{index !== steps.length - 1 && (<Step.Spacer className="-translate-y-7 sm:w-32"/>)}
				</Fragment>))}
		</div>);
};
const VerifySteamSteps = () => {
    return (<>
			<VerifySteamStepsMobile />
			<VerifySteamStepsDesktop />
		</>);
};
const VerifySteamStageSuccess = () => {
    const { onEnterGiveaway, isLoading } = useVerifySteamStageSuccess();
    return (<div className="flex flex-col justify-center items-center gap-8">
			<Image className="p-4" src={SuccessIcon.src} width={160} height={160} alt="Error icon"/>
			<div className="flex flex-col gap-2 text-center">
				<span className="font-mono uppercase text-3xl font-bold">Success</span>
				<span className="text-light-4 text-sm"> Your Steam account has been successfully verified!<br />
					You can now participate in the giveaway
				</span>
			</div>
			<ButtonNew className="w-52 h-12" disabled={isLoading} onClick={onEnterGiveaway}>
				{isLoading ? <LoadingCircles width={20} height={20}/> : 'Enter Giveaway'}
			</ButtonNew>
		</div>);
};
const VerifySteamStageErrorHelper = ({ code }) => {
    if (code === SteamErrorCode.ProfileNotPublic) {
        return (<div className="text-sm flex flex-col justify-center gap-3 px-2">
				<div>
					<span className="text-pink font-bold text-sm text-center">
						<p className="text-pink font-bold text-sm text-center">Verification failed</p>
						<p className="text-pink font-bold text-sm text-center">Your Steam profile is not public</p>
					</span>
				</div>
				To verify your Steam account, take these steps:
				<ol className="flex flex-col list-decimal px-6">
					<li>Go to{' '}
						<a className="underline text-link" target="_blank" rel="noreferrer" href="https://steamcommunity.com/my/edit/settings">
							Steam privacy settings
						</a>.
					</li>
					<li>
						Make sure “My Profile” is public.
					</li>
				</ol>
				<img width={600} height={400} src={SteamPrivacySettingsExample.src} alt="Steam privacy settings example"/>
				<SignInWithSteam />
			</div>);
    }
    return null;
};
const VerifySteamStageError = ({ error: errorProp, code: codeProp }) => {
    const hookData = useVerifySteamStageError();
    const error = errorProp || hookData.error;
    const code = codeProp || hookData.code;
    if (code) {
        return <VerifySteamStageErrorHelper code={code}/>;
    }
    return (<div className="flex flex-col justify-center items-center gap-4 pb-8">
			<Image className="p-4" src={ErrorIcon.src} width={200} height={200} alt="Error icon"/>
			<span className="font-mono uppercase text-3xl font-bold">Error</span>
			<span className="text-pink text-sm text-center">Verification failed<br />
				{error}
			</span>
			<SignInWithSteam />
		</div>);
};
const VerifySteamStageSignIn = () => {
    return (<div className="flex flex-col justify-center items-center gap-6">
			<Image src={SilverCup.src} width={200} height={200} alt="Silver cup"/>
			<span className="text-light-4 text-sm text-center">After you sign in with STEAM, make sure your profile is set to<br />public.
				You could do so by going <a className="text-white underline" target="_blank" href="https://steamcommunity.com/my/edit/settings" rel="noreferrer">here</a>.
			</span>
			<SignInWithSteam />
		</div>);
};
const VerifySteamStageSection = ({ stage: stageProp, error, code }) => {
    const hookData = useVerifySteamModal();
    const stage = stageProp !== null && stageProp !== void 0 ? stageProp : hookData.stage;
    switch (stage) {
        case VerifySteamStage.SIGN_IN:
            return <VerifySteamStageSignIn />;
        case VerifySteamStage.SUCCESS:
            return <VerifySteamStageSuccess />;
        case VerifySteamStage.ERROR:
            return <VerifySteamStageError error={error} code={code}/>;
        default:
            return null;
    }
};
export default VerifySteamModal;
